import { useRef, useEffect } from "react";
import { AlertCircle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useChatStream } from "@/hooks/useChatStream";
import { ChatMessage } from "./ChatMessage";
import { ChatInput } from "./ChatInput";
import { ChatEmptyState } from "./ChatEmptyState";
import { ToolCallIndicator } from "./ToolCallIndicator";

interface ChatPanelProps {
  collectionId: string;
}

export function ChatPanel({ collectionId }: ChatPanelProps) {
  const {
    messages,
    isStreaming,
    currentToolCall,
    error,
    sendMessage,
    stop,
  } = useChatStream({ collectionId });

  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages or streaming
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, currentToolCall]);

  return (
    <div className="flex flex-col h-full">
      {messages.length === 0 ? (
        <ChatEmptyState onSendMessage={sendMessage} />
      ) : (
        <ScrollArea className="flex-1">
          <div ref={scrollRef} className="flex flex-col gap-3 p-3">
            {messages.map((msg) => (
              <ChatMessage key={msg.id} message={msg} />
            ))}
            {currentToolCall && (
              <ToolCallIndicator toolName={currentToolCall.name} />
            )}
          </div>
        </ScrollArea>
      )}

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 text-xs text-destructive bg-destructive/10 border-t">
          <AlertCircle className="h-3 w-3 shrink-0" />
          <span className="truncate">{error}</span>
        </div>
      )}

      <ChatInput
        onSend={sendMessage}
        onStop={stop}
        isStreaming={isStreaming}
      />
    </div>
  );
}
